import { useEventCallback } from '@mui/material';
import { FormRecapVM } from 'practice/view-models/pratice-recap';
import { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import AccordionRecap from './accordion-recap/index';
import { PmiButton } from 'shell/components/shared/button';
import { toast } from 'react-toastify';
import { ContactData, PracticeData } from 'shell/models/practice';
import { useFormik } from 'formik';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { recapValidationSchema } from 'practice/constants/practice-recap';
import { TypeRegister } from 'practice/constants/type-register';
import { ResidenceID } from 'practice/constants/id-practice-edit-residence';
import {
  PracticeDraftBEStep,
  PracticeDraftStep,
} from 'practice/constants/practice-management-step';

interface StepRecapProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  identifierAnpr?: string;
  onBack: (step?: PracticeDraftStep) => void;
  onNext: () => void;
  disabledAll?: boolean;
  iniziativa?: string;
  fromPerson?: string;
  setIsAcceptMessi: (bool: boolean) => void;
  ForPracticeTemporary?: boolean;
}

const StepRecapComponent = ({
  onBack,
  onNext,
  codeEnte,
  yearId,
  typePractice,
  typeRegister,
  progressive,
  identifierAnpr,
  disabledAll,
  iniziativa,
  fromPerson,
  setIsAcceptMessi,
  ForPracticeTemporary,
}: StepRecapProps): ReactElement => {
  const updateContacts = useHttpClient(
    `${__PRACTICE_API_URL__}/pratiche/contatti/aggiornamento-inserimento`,
    {
      options: { method: 'POST' },
    }
  );

  const [disabledForward, setDisabledForward] = useState(true);

  const [contacts] = useHttpFetch<HttpResponse<ContactData>, FormRecapVM>(
    `${__PRACTICE_API_URL__}/pratiche/contatti/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return {
          isRiepilogo: response.data.payload?.isRiepilogo || undefined, // 1 se il riepilogo è stato stampato
          telephone: response.data.payload?.numTelefono || undefined,
          phone: response.data.payload?.numCellulare || undefined,
          email: response.data.payload?.email || undefined,
          address: response.data.payload?.indirizzo || undefined,
          province: response.data.payload?.provincia || undefined,
          municipality: response.data.payload?.comune || undefined,
          fax: response.data.payload?.numeroFax || undefined,
          pec: response.data.payload?.pec || undefined,
          note: response.data.payload?.noteContatto || undefined,
          isFromService: Boolean(
            response.data.payload?.numTelefono ||
              response.data.payload?.numCellulare ||
              response.data.payload?.email ||
              response.data.payload?.pec ||
              response.data.payload?.noteContatto
          ),
        };
      },
    }
  );

  const [pratica] = useHttpFetch<HttpResponse<PracticeData>, PracticeData>(
    `${__PRACTICE_API_URL__}/pratiche/pratica/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  useEffect(() => {
    setDisabledForward((oldState) => {
      if (typeRegister === TypeRegister.Emigration) {
        return false;
      }

      if ((contacts.data as FormRecapVM)?.isRiepilogo === '1') {
        return false;
      }

      // convivenza: stampa Domanda di apertura non obbligatoria, abilitare subito Avanti
      if (
        (pratica.data as PracticeData)?.tipoCasoPratica?.toString() ===
        ResidenceID.domesticPartnership
      ) {
        return false;
      }

      return oldState;
    });
  }, [contacts.data, pratica.data, disabledForward, typeRegister]);

  const onSubmitBozza = useEventCallback((valuesForm: FormRecapVM) => {
    (
      updateContacts({
        data: {
          indirizzo: valuesForm.address || undefined,
          provincia: valuesForm.province || undefined,
          comune: valuesForm.municipality || undefined,
          numTelefono: valuesForm.telephone || undefined,
          numCellulare: valuesForm.phone || undefined,
          email: valuesForm.email || undefined,
          pec: valuesForm.pec || undefined,
          numeroFax: valuesForm.fax || undefined,
          noteContatto: valuesForm.note || undefined,
          key: {
            codiceEnte: codeEnte || undefined,
            tipoRegistro: typeRegister || undefined,
            idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
            progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
          },
          stepBozza: PracticeDraftBEStep.Recap,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
    });
  });

  const onSubmit = useEventCallback((valuesForm: FormRecapVM) => {
    (
      updateContacts({
        data: {
          numTelefono: valuesForm.telephone || undefined,
          numCellulare: valuesForm.phone || undefined,
          email: valuesForm.email || undefined,
          pec: valuesForm.pec || undefined,
          noteContatto: valuesForm.note || undefined,
          numeroFax: valuesForm.fax || undefined,
          indirizzo: valuesForm.address || undefined,
          comune: valuesForm.municipality || undefined,
          provincia: valuesForm.province || undefined,
          key: {
            codiceEnte: codeEnte || undefined,
            tipoRegistro: typeRegister || undefined,
            idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
            progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
          },
          stepBozza: PracticeDraftBEStep.Recap,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      setIsAcceptMessi(true);
      toast.success('Operazione eseguita con successo');

      onNext();
    });
  });

  const formContext = useFormik<FormRecapVM>({
    initialValues: (contacts.data || {}) as FormRecapVM,
    enableReinitialize: true,
    validationSchema: recapValidationSchema,
    onSubmit,
  });

  const disabled = useMemo(() => {
    if (disabledAll) {
      return true;
    }

    // if (iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm) {
    //   // ANPR o ELIXFORM
    //   return true;
    // }

    return false;
  }, [disabledAll, iniziativa]);

  return (
    <>
      <AccordionRecap
        codeEnte={codeEnte}
        yearId={yearId}
        typeRegister={typeRegister}
        progressive={progressive}
        typePractice={typePractice}
        identifierAnpr={identifierAnpr}
        formContext={formContext}
        setDisabledForward={setDisabledForward}
        onSubmit={onSubmitBozza}
        disabled={disabled}
        fromPerson={fromPerson}
      />
      <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
        <div className="pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrand"
            onClick={() =>
              onBack(
                typeRegister !== TypeRegister.Emigration && !ForPracticeTemporary
                  ? PracticeDraftStep.AccertamentoMessi
                  : undefined
              )
            }
          >
            Indietro
          </PmiButton>
        </div>
        <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="cdmBrandThird"
            onClick={() => {
              if (disabledAll) {
                // onNext();

                return;
              }
              formContext.handleSubmit();
            }}
            disabled={disabledForward}
          >
            Avanti
          </PmiButton>
        </div>
      </div>
    </>
  );
};

StepRecapComponent.displayName = 'step component';

const StepRecap = memo(StepRecapComponent) as typeof StepRecapComponent;

export default StepRecap;
